import Phaser from 'phaser';
import type { FactionId } from '../../shared/types';

/**
 * Faction emblem reveal — scale 0 → 0.8 + spin 360° over 600ms,
 * sits at (x, y) for the rest of scene. Loads the AI-gen emblem PNG
 * lazily if not yet cached.
 *
 * Returns the Image (or null if creation failed in test harness).
 */
export const EMBLEM_REVEAL_DURATION_MS = 600;
export const EMBLEM_TARGET_SCALE = 0.8;
const EMBLEM_TEXTURE_PREFIX = 'emblem-';

export function emblemTextureKey(faction: FactionId): string {
  return EMBLEM_TEXTURE_PREFIX + faction;
}

export function emblemAssetPath(faction: FactionId): string {
  return `assets/ai-gen/emblems/emblem-${faction}.png`;
}

/**
 * Reveal a faction emblem at (x, y). The texture is expected to be either
 * already loaded OR will be queued via `scene.load` and the visual creation
 * deferred to the `complete` callback. In test harness we synchronously
 * create + tween a placeholder Image so test assertions remain stable.
 *
 * @returns the Image object (or null if scene lacks add.image / load).
 */
export function revealEmblem(
  scene: Phaser.Scene,
  faction: FactionId,
  x: number,
  y: number,
): Phaser.GameObjects.Image | null {
  const key = emblemTextureKey(faction);

  // If the texture exists OR the test harness lacks a load manager, create
  // the image directly. Otherwise queue the asset and create on complete.
  const alreadyLoaded = (() => {
    try { return scene.textures?.exists?.(key) === true; } catch { return false; }
  })();

  const create = (): Phaser.GameObjects.Image | null => {
    let img: Phaser.GameObjects.Image;
    try {
      img = scene.add.image(x, y, key);
    } catch {
      return null;
    }
    try {
      if (typeof (img as { setScale?: unknown }).setScale === 'function') img.setScale(0);
      if (typeof (img as { setDepth?: unknown }).setDepth === 'function') img.setDepth(15);
      if (typeof (img as { setAlpha?: unknown }).setAlpha === 'function') img.setAlpha(0);
    } catch { /* no-op for partial mocks */ }

    try {
      scene.tweens.add({
        targets: img,
        scale: EMBLEM_TARGET_SCALE,
        rotation: Math.PI * 2,
        alpha: 1,
        duration: EMBLEM_REVEAL_DURATION_MS,
        ease: 'Back.easeOut',
      });
    } catch { /* harness lacks tweens.add */ }
    return img;
  };

  if (alreadyLoaded) {
    return create();
  }

  // Lazy-load path: schedule the asset, create when ready.
  try {
    if (scene.load && typeof scene.load.image === 'function') {
      scene.load.image(key, emblemAssetPath(faction));
      scene.load.once('complete', () => { create(); });
      if (typeof scene.load.start === 'function') scene.load.start();
      return null;  // image will be created asynchronously
    }
  } catch { /* harness: load mgr missing */ }
  // Final fallback — try synchronous create even without texture (will
  // render as a missing-texture placeholder rather than crash).
  return create();
}

/**
 * Pure helper: compute the list of skill IDs that levelled up between two
 * snapshots. Defensive — missing prev IDs are treated as level 0.
 *
 * Returns one entry per leveled-up skill (no dedup; if a skill jumps
 * multiple levels it still only produces ONE entry with from→to range).
 */
export interface SkillLevelDiff {
  skillId: string;
  fromLevel: number;
  toLevel: number;
}

export function diffSkillLevels(
  prev: Record<string, number> | null | undefined,
  next: Record<string, number> | null | undefined,
): SkillLevelDiff[] {
  if (!next) return [];
  const prevMap = prev ?? {};
  const out: SkillLevelDiff[] = [];
  for (const [skillId, toLevel] of Object.entries(next)) {
    const fromLevel = prevMap[skillId] ?? 0;
    if (toLevel > fromLevel) {
      out.push({ skillId, fromLevel, toLevel });
    }
  }
  return out;
}

/**
 * Floating「⚡ Lv.X → Lv.Y!」 text per leveled-up skill, stacked vertically
 * starting at (x, y). Returns the array of text objects (or empty if none).
 */
export function showLevelUpFloats(
  scene: Phaser.Scene,
  diffs: SkillLevelDiff[],
  x: number,
  y: number,
): Phaser.GameObjects.Text[] {
  const out: Phaser.GameObjects.Text[] = [];
  for (let i = 0; i < diffs.length; i++) {
    const d = diffs[i];
    const txtStr = `⚡ ${d.skillId} Lv.${d.fromLevel} → Lv.${d.toLevel}!`;
    let t: Phaser.GameObjects.Text;
    try {
      t = scene.add.text(x, y + i * 36, txtStr, {
        fontSize: '24px', color: '#ffd54f', fontStyle: 'bold', fontFamily: 'sans-serif',
      });
      if (typeof (t as { setOrigin?: unknown }).setOrigin === 'function') t.setOrigin(0.5);
      if (typeof (t as { setDepth?: unknown }).setDepth === 'function') t.setDepth(18);
    } catch { continue; }
    try {
      scene.tweens.add({
        targets: t,
        y: y + i * 36 - 24,
        alpha: 0,
        duration: 2200,
        ease: 'Cubic.easeOut',
        delay: i * 250,
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* noop */ }
    out.push(t);
  }
  return out;
}

const SKILL_SNAPSHOT_KEY = 'eternal-fortress.pre-battle-skill-levels';

/**
 * Persist current skillLevels snapshot to localStorage so ResultScene can
 * diff after the battle. Caller responsibility: invoke at battle start.
 */
export function snapshotSkillLevels(skillLevels: Record<string, number>): void {
  try {
    localStorage.setItem(SKILL_SNAPSHOT_KEY, JSON.stringify(skillLevels));
  } catch { /* storage unavailable */ }
}

export function loadLastSkillLevelsSnapshot(): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(SKILL_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) as Record<string, number> : null;
  } catch { return null; }
}
